<template>
  <div class="heartbeat-trace" :class="[tone, { compact }]" aria-hidden="true">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
const ECG_WAVEFORM = [0, 0, 0, 0.08, -0.12, 0.18, 0, 0, 0.16, 1, -0.92, 0.48, -0.18, 0, 0, 0.08, 0, 0]

export default {
  name: 'GroundStationHeartbeatTrace',
  props: {
    tone: { type: String, default: 'muted' },
    pulseKey: { type: [Number, String], default: '' },
    compact: { type: Boolean, default: false }
  },
  data () {
    return {
      samples: [],
      pendingSamples: [],
      animationFrame: null,
      resizeObserver: null,
      lastSampleAt: 0,
      canvasWidth: 0,
      canvasHeight: 0
    }
  },
  computed: {
    sampleSpacing () { return this.compact ? 2.5 : 3 }
  },
  watch: {
    pulseKey: {
      immediate: true,
      handler (value, oldValue) {
        if (!value || value === oldValue) return
        this.pendingSamples.push(...ECG_WAVEFORM)
        if (this.pendingSamples.length > 90) this.pendingSamples.splice(0, this.pendingSamples.length - 90)
      }
    }
  },
  mounted () {
    this.resizeCanvas()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.resizeCanvas)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.resizeCanvas)
    }
    this.animationFrame = requestAnimationFrame(this.draw)
  },
  beforeDestroy () {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    else window.removeEventListener('resize', this.resizeCanvas)
  },
  methods: {
    resizeCanvas () {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const rect = this.$el.getBoundingClientRect()
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      this.canvasWidth = Math.max(1, rect.width)
      this.canvasHeight = Math.max(1, rect.height)
      canvas.width = Math.round(this.canvasWidth * ratio)
      canvas.height = Math.round(this.canvasHeight * ratio)
      canvas.style.width = `${this.canvasWidth}px`
      canvas.style.height = `${this.canvasHeight}px`
      canvas.getContext('2d').setTransform(ratio, 0, 0, ratio, 0, 0)
      this.fitSamples()
    },
    fitSamples () {
      const count = Math.max(2, Math.ceil(this.canvasWidth / this.sampleSpacing))
      if (this.samples.length > count) this.samples.splice(0, this.samples.length - count)
      while (this.samples.length < count) this.samples.unshift(0)
    },
    pushSample () {
      this.samples.push(this.pendingSamples.length ? this.pendingSamples.shift() : 0)
      const count = Math.max(2, Math.ceil(this.canvasWidth / this.sampleSpacing))
      if (this.samples.length > count) this.samples.shift()
    },
    draw (timestamp) {
      const interval = this.compact ? 24 : 20
      if (!this.lastSampleAt) this.lastSampleAt = timestamp
      while (timestamp - this.lastSampleAt >= interval) {
        this.pushSample()
        this.lastSampleAt += interval
      }
      this.paint()
      this.animationFrame = requestAnimationFrame(this.draw)
    },
    paint () {
      const canvas = this.$refs.canvas
      if (!canvas || !this.canvasWidth || !this.canvasHeight) return
      const context = canvas.getContext('2d')
      const color = getComputedStyle(this.$el).color
      const center = this.canvasHeight / 2
      const amplitude = this.canvasHeight * 0.42
      context.clearRect(0, 0, this.canvasWidth, this.canvasHeight)

      context.beginPath()
      context.moveTo(0, center)
      context.lineTo(this.canvasWidth, center)
      context.strokeStyle = this.colorWithAlpha(color, 0.16)
      context.lineWidth = 1
      context.stroke()

      const gradient = context.createLinearGradient(0, 0, this.canvasWidth, 0)
      gradient.addColorStop(0, this.colorWithAlpha(color, 0))
      gradient.addColorStop(0.18, this.colorWithAlpha(color, 0.18))
      gradient.addColorStop(0.55, this.colorWithAlpha(color, 0.62))
      gradient.addColorStop(1, color)
      context.beginPath()
      this.samples.forEach((sample, index) => {
        const x = index * this.sampleSpacing
        const y = center - sample * amplitude
        if (index === 0) context.moveTo(x, y)
        else context.lineTo(x, y)
      })
      context.strokeStyle = gradient
      context.lineWidth = this.compact ? 1.25 : 1.5
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.shadowColor = color
      context.shadowBlur = this.compact ? 3 : 5
      context.stroke()
      context.shadowBlur = 0

      const lastSample = this.samples[this.samples.length - 1] || 0
      context.beginPath()
      context.arc(this.canvasWidth - 1.8, center - lastSample * amplitude, this.compact ? 1.35 : 1.8, 0, Math.PI * 2)
      context.fillStyle = color
      context.shadowColor = color
      context.shadowBlur = this.compact ? 4 : 7
      context.fill()
      context.shadowBlur = 0
    },
    colorWithAlpha (color, alpha) {
      const values = color.match(/[\d.]+/g)
      if (!values || values.length < 3) return color
      return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`
    }
  }
}
</script>

<style lang="scss" scoped>
.heartbeat-trace {
  width: 100%;
  height: 40px;
  position: absolute;
  inset: 0;
  color: rgba(var(--ui-text-secondary-rgb), .58);
  opacity: .72;
  pointer-events: none;
}

.heartbeat-trace canvas {
  position: absolute;
  inset: 0;
  display: block;
  transform: translateY(-6px);
}

.excellent { color: rgba(var(--ui-color-primary-rgb), .78); }
.good { color: rgba(var(--ui-color-warning-rgb), .82); }
.poor { color: rgba(var(--ui-color-danger-rgb), .86); }
.compact { opacity: .48; }

@media (prefers-reduced-motion: reduce) {
  .heartbeat-trace { opacity: .45; }
}
</style>
